const OPTION_KEY = "app-feed-components";
const LEGACY_OPTION_KEY = "app-feed-component";
const DEFAULT_KEY = "default";

const SOURCE_FEED_ITEM = "feedItem";
const SOURCE_CONSTANT = "constant";
const SOURCE_CONTEXT = "context";
const SOURCE_CONTEXT_FIELD = "context_field";

const SOURCES = [SOURCE_FEED_ITEM, SOURCE_CONSTANT, SOURCE_CONTEXT, SOURCE_CONTEXT_FIELD];

const REQUIRED_SOURCE_PARAMS = {
  [SOURCE_FEED_ITEM]: [],
  [SOURCE_CONSTANT]: ["entityType", "entityPkVars"],
  [SOURCE_CONTEXT]: ["sourceContext"],
  [SOURCE_CONTEXT_FIELD]: ["sourceContext", "sourceField"],
};

const PRIMITIVE_REQUIRED_SOURCE_PARAMS = {
  [SOURCE_FEED_ITEM]: ["sourceField"],
  [SOURCE_CONSTANT]: ["value"],
  [SOURCE_CONTEXT]: ["sourceContext"],
  [SOURCE_CONTEXT_FIELD]: ["sourceContext", "sourceField"],
};

const FEED_ITEM_SCOPE_KEY = "feed item";

const LEAF_FIELDS = ["componentId", "requiredContextSourcing"];

const LEGACY_FIELDS = [
  "componentId",
  "componentName",
  "contextVarName",
  "contexts",
  "materializedComponentId",
  "storyTypeCards",
  "storyTypeCardContexts",
  "storyTypeSubVariantCards",
  "storyTypeSubVariantContexts",
];

const RequiredContextValue = require("./required-context-value");
const { PRIMITIVE_CONTEXT_TYPES, requiredContextValueType } = require("../helpers/required-context-entity-type");

const LEGACY_ONLY_FIELDS = LEGACY_FIELDS.filter(field => !LEAF_FIELDS.includes(field));

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function positiveId(raw) {
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

class FeedComponentsMap {


  static get OPTION_KEY() {
    return OPTION_KEY;
  }

  static get LEGACY_OPTION_KEY() {
    return LEGACY_OPTION_KEY;
  }

  static get DEFAULT_KEY() {
    return DEFAULT_KEY;
  }

  static get SOURCES() {
    return [...SOURCES];
  }


  static isLeaf(node) {
    return isPlainObject(node) && "componentId" in node;
  }

  static entityKey(entityType) {
    return String(entityType ?? "").replace(/^Legacy/, "").toLowerCase();
  }

  static rowStoryType({ itemStoryType = "", entityStoryType = "", feedStoryType = "" } = {}) {
    return [itemStoryType, entityStoryType, feedStoryType].find(type => typeof type === "string" && type !== "") ?? "";
  }

  static resolve(map, { entityType = "", type = "", subtype = "" } = {}) {
    if (!isPlainObject(map)) {
      return null;
    }
    const entity = FeedComponentsMap.entityKey(entityType);
    const candidates = [
      [entity, type, subtype],
      [entity, type, DEFAULT_KEY],
      [entity, type],
      [entity, DEFAULT_KEY, subtype],
      [entity, DEFAULT_KEY, DEFAULT_KEY],
      [entity, DEFAULT_KEY],
      [entity],
      [DEFAULT_KEY],
    ];
    for (const candidate of candidates) {
      if (candidate.some(segment => !segment)) {
        continue;
      }
      const node = candidate.reduce((current, segment) => {
        return isPlainObject(current) ? current[segment] : undefined;
      }, map);
      if (FeedComponentsMap.isLeaf(node)) {
        return { leaf: node, path: candidate };
      }
    }
    return null;
  }

  // XXX: THE one detector for "this feed is misconfigured". The canvas block and
  // XXX: the layers-panel row colour BOTH read it, so they cannot disagree --
  // XXX: a broken feed used to paint a red card on canvas while its layers row
  // XXX: stayed white, leaving the author no way to find it in the tree.
  static legacyPayloadIn(optionsTree) {
    for (const themeMode of Object.keys(optionsTree ?? {})) {
      for (const breakpoints of Object.values(optionsTree[themeMode] ?? {})) {
        for (const states of Object.values(breakpoints ?? {})) {
          const leaf = states?.[LEGACY_OPTION_KEY];
          const value = leaf && typeof leaf === "object" && "r" in leaf ? leaf.r : leaf;
          if (isPlainObject(value)) {
            return value;
          }
        }
      }
    }
    return null;
  }

  // XXX: THE reader for a feed's component map out of a CRI options tree
  // XXX: (themeMode -> breakpoint -> state -> optionKey -> {r, c}). Three call
  // XXX: sites referenced `FeedComponentsMap.readOptionValue` while it was
  // XXX: defined NOWHERE; two of them guarded the call with
  // XXX: `FeedComponentsMap.readOptionValue ? ... : ...`, and the else-arm of the
  // XXX: one on DynamicAppFeedElement passed `null` -- so problemsForOptions took
  // XXX: its empty-map branch and EVERY feed element on EVERY destination
  // XXX: reported "no app-feed-components mapping" no matter what it stored,
  // XXX: while the picker modal showed the real binding. The author saw an error
  // XXX: they could not clear and a selection that would not stick. See #112.
  static readOptionValue(optionsTree) {
    for (const themeMode of Object.keys(optionsTree)) {
      for (const breakpoints of Object.values(optionsTree[themeMode])) {
        for (const states of Object.values(breakpoints)) {
          const leaf = states[OPTION_KEY];
          if (FeedComponentsMap.#isInheritMarker(leaf)) {
            continue;
          }
          const value = leaf && typeof leaf === "object" && "r" in leaf ? leaf.r : leaf;
          if (isPlainObject(value)) {
            return value;
          }
        }
      }
    }
    return null;
  }

  static problemsForOptions(optionsTree, map) {
    const legacy = FeedComponentsMap.legacyPayloadIn(optionsTree);
    if (legacy) {
      return [
        `this feed still holds the retired "${LEGACY_OPTION_KEY}" payload `
        + `(${Object.keys(legacy).join(", ")}). Re-pick its component(s) so the mapping is stored as `
        + `"${OPTION_KEY}", then recompile the destination.`,
      ];
    }
    // XXX: PHP json_encodes an empty map as [], which is TRUTHY -- so a bare
    // XXX: `!map` let it through and the feed rendered no rows at all on staging
    // XXX: while the canvas called it broken. Emptiness is unmapped, not mapped.
    if (!map || Object.keys(map).length === 0) {
      return [
        `no "${OPTION_KEY}" mapping — this feed has no component to render its rows. `
        + "Select a Card Component in the element inspector.",
      ];
    }
    return FeedComponentsMap.problems(map);
  }

  static problems(map, pathPrefix = []) {
    if (map === null || map === undefined) {
      return [];
    }
    if (!isPlainObject(map)) {
      return [`${OPTION_KEY}${FeedComponentsMap.#pathLabel(pathPrefix)} must be an object, got ${typeof map}`];
    }
    const problems = [];
    const legacy = LEGACY_ONLY_FIELDS.filter(field => field in map);
    if (legacy.length) {
      problems.push(
        `${OPTION_KEY}${FeedComponentsMap.#pathLabel(pathPrefix)} carries the retired field(s) `
        + `${legacy.join(", ")} — this feed still holds the pre-${OPTION_KEY} payload. Re-pick its `
        + `component(s) so the mapping is stored as ${OPTION_KEY}.<entityType>.<type>.<subtype> leaves `
        + "of { componentId, requiredContextSourcing }, then recompile the destination.",
      );
    }
    if (FeedComponentsMap.#isLeafIntent(map)) {
      return [...problems, ...FeedComponentsMap.#leafProblems(map, pathPrefix)];
    }
    Object.entries(map).forEach(([key, node]) => {
      if (LEGACY_ONLY_FIELDS.includes(key)) {
        return;
      }
      problems.push(...FeedComponentsMap.problems(node, [...pathPrefix, key]));
    });
    return problems;
  }

  static missingSourcingProblems(leaf, requiredContextNames = []) {
    const sourcing = isPlainObject(leaf?.requiredContextSourcing) ? leaf.requiredContextSourcing : {};
    return (requiredContextNames ?? [])
        .filter(name => name && !(name in sourcing))
        .map(name => `required context "${name}" of component ${leaf?.componentId} has no `
          + "requiredContextSourcing entry — the row would mount with it unmet");
  }

  static kindMismatchProblems(leaf, requiredDefs = {}) {
    const sourcing = isPlainObject(leaf?.requiredContextSourcing) ? leaf.requiredContextSourcing : {};
    const problems = [];
    Object.entries(sourcing).forEach(([name, entry]) => {
      if (!isPlainObject(requiredDefs?.[name]) || !isPlainObject(entry)) {
        return;
      }
      const label = `required context "${name}" of component ${leaf?.componentId}`;
      const requiredValueType = requiredContextValueType(requiredDefs[name]);
      if (requiredValueType && !PRIMITIVE_CONTEXT_TYPES.includes(requiredValueType)) {
        problems.push(`${label} is a ${requiredValueType}, which a feed row cannot source`);
        return;
      }
      if (requiredValueType && !entry.valueType) {
        problems.push(`${label} is a ${requiredValueType}, but its sourcing entry `
          + `${JSON.stringify(entry)} sources an entity. Re-pick the card and source it as a ${requiredValueType} value`);
        return;
      }
      if (!requiredValueType && entry.valueType) {
        problems.push(`${label} is an entity, but its sourcing entry sources a ${entry.valueType} value`);
        return;
      }
      if (requiredValueType && entry.valueType !== requiredValueType) {
        problems.push(`${label} is a ${requiredValueType}, but its sourcing entry sources a ${entry.valueType}`);
      }
    });
    return problems;
  }

  static describeSourcing(entry) {
    if (!isPlainObject(entry)) {
      return "";
    }
    switch (entry.source) {
      case SOURCE_FEED_ITEM:
        return entry.sourceField ? `Feed Item->${entry.sourceField}` : "Feed Item";
      case SOURCE_CONSTANT:
        if (entry.valueType) {
          return `${JSON.stringify(entry.value)} (constant)`;
        }
        return `${entry.entityType ?? "?"} #${entry.entityPkVars?.id ?? "?"} (constant)`;
      case SOURCE_CONTEXT:
        return `${entry.sourceContext} (context)`;
      case SOURCE_CONTEXT_FIELD:
        return `${entry.sourceContext}->${entry.sourceField} (context field)`;
      default:
        return String(entry.source ?? "");
    }
  }

  static resolveRowContexts({ leaf, row, enclosingContexts = {}, walkField = null }) {
    const sourcing = isPlainObject(leaf?.requiredContextSourcing) ? leaf.requiredContextSourcing : {};
    const contexts = {};
    const problems = [];
    let pending = Object.entries(sourcing);
    let progressed = true;
    while (pending.length && progressed) {
      progressed = false;
      const stillPending = [];
      for (const [name, entry] of pending) {
        const scope = { ...enclosingContexts, ...contexts };
        const outcome = FeedComponentsMap.#resolveEntry({ name, entry, row, scope, walkField });
        if (outcome.deferred) {
          stillPending.push([name, entry]);
          continue;
        }
        progressed = true;
        if (outcome.problem) {
          problems.push(outcome.problem);
          continue;
        }
        contexts[name] = outcome.value;
      }
      pending = stillPending;
    }
    pending.forEach(([name, entry]) => {
      problems.push(
        `context "${name}" cannot be resolved: its ${entry?.source} source names "${entry?.sourceContext}", `
        + "which is neither provided by the enclosing component nor resolvable from this row "
        + "(a missing context, or a cycle between sourcing entries)",
      );
    });
    return { contexts, problems };
  }

  static sourcingFromBinding(binding, { rowContextName = null } = {}) {
    if (!isPlainObject(binding)) {
      return null;
    }
    if (RequiredContextValue.isPrimitiveBinding(binding)) {
      return FeedComponentsMap.#primitiveSourcingFromBinding(binding, rowContextName);
    }
    if (binding.entitySource === SOURCE_CONSTANT) {
      return {
        source: SOURCE_CONSTANT,
        entityType: binding.entityType,
        entityPkVars: binding.entityPkVars,
      };
    }
    if (binding.entitySource !== SOURCE_CONTEXT || !binding.sourceContext) {
      return null;
    }
    if (binding.sourceField) {
      return {
        source: SOURCE_CONTEXT_FIELD,
        sourceContext: binding.sourceContext,
        sourceField: binding.sourceField,
      };
    }
    if (rowContextName && binding.sourceContext === rowContextName) {
      return { source: SOURCE_FEED_ITEM };
    }
    return { source: SOURCE_CONTEXT, sourceContext: binding.sourceContext };
  }

  static sourcingFromBindings(bindings, { rowContextName = null } = {}) {
    const sourcing = {};
    Object.entries(bindings ?? {}).forEach(([name, binding]) => {
      const entry = FeedComponentsMap.sourcingFromBinding(binding, { rowContextName });
      if (entry) {
        sourcing[name] = entry;
      }
    });
    return sourcing;
  }

  static bindingFromSourcing(sourcing, { rowContextName = null, entityType = null, valueType = null } = {}) {
    if (!isPlainObject(sourcing)) {
      return null;
    }
    if (sourcing.valueType || valueType) {
      return FeedComponentsMap.#primitiveBindingFromSourcing(sourcing, {
        rowContextName,
        valueType: sourcing.valueType ?? valueType,
      });
    }
    if (sourcing.source === SOURCE_CONSTANT) {
      return {
        entityType: sourcing.entityType ?? entityType,
        entitySource: SOURCE_CONSTANT,
        entityPkVars: sourcing.entityPkVars,
      };
    }
    if (sourcing.source === SOURCE_FEED_ITEM) {
      return { entityType, entitySource: SOURCE_CONTEXT, sourceContext: rowContextName };
    }
    if (sourcing.source === SOURCE_CONTEXT) {
      return { entityType, entitySource: SOURCE_CONTEXT, sourceContext: sourcing.sourceContext };
    }
    if (sourcing.source === SOURCE_CONTEXT_FIELD) {
      return {
        entityType,
        entitySource: SOURCE_CONTEXT,
        sourceContext: sourcing.sourceContext,
        sourceField: sourcing.sourceField,
      };
    }
    return null;
  }

  static buildBranch(entityType, rows = [], subRows = []) {
    const entity = FeedComponentsMap.entityKey(entityType);
    const nodes = {};
    const leafOf = row => {
      const leaf = { componentId: Number(row.componentId) };
      if (row.requiredContextSourcing) {
        leaf.requiredContextSourcing = row.requiredContextSourcing;
      }
      return leaf;
    };
    rows.forEach(row => {
      nodes[row.typeKey || DEFAULT_KEY] = { base: leafOf(row), statuses: {} };
    });
    subRows.forEach(row => {
      const key = row.typeKey || DEFAULT_KEY;
      if (!nodes[key]) {
        nodes[key] = { base: null, statuses: {} };
      }
      nodes[key].statuses[row.status] = leafOf(row);
    });
    const branch = {};
    Object.entries(nodes).forEach(([key, node]) => {
      const statuses = Object.keys(node.statuses);
      if (!statuses.length) {
        if (node.base) {
          branch[key] = node.base;
        }
        return;
      }
      branch[key] = { ...node.statuses };
      if (node.base) {
        branch[key][DEFAULT_KEY] = node.base;
      }
    });
    return { [entity]: branch };
  }

  static readBranch(map, entityType) {
    const branch = isPlainObject(map) ? map[FeedComponentsMap.entityKey(entityType)] : null;
    const rows = [];
    const subRows = [];
    const rowOf = (typeKey, leaf, status = null) => {
      const row = { typeKey: typeKey === DEFAULT_KEY ? "" : typeKey, componentId: leaf.componentId };
      if (status) {
        row.status = status;
      }
      if (leaf.requiredContextSourcing) {
        row.requiredContextSourcing = leaf.requiredContextSourcing;
      }
      return status ? { typeKey: row.typeKey, status, componentId: row.componentId, requiredContextSourcing: row.requiredContextSourcing } : row;
    };
    Object.entries(isPlainObject(branch) ? branch : {}).forEach(([typeKey, node]) => {
      if (FeedComponentsMap.isLeaf(node)) {
        rows.push(rowOf(typeKey, node));
        return;
      }
      Object.entries(isPlainObject(node) ? node : {}).forEach(([statusKey, leaf]) => {
        if (!FeedComponentsMap.isLeaf(leaf)) {
          return;
        }
        if (statusKey === DEFAULT_KEY) {
          rows.push(rowOf(typeKey, leaf));
          return;
        }
        subRows.push(rowOf(typeKey, leaf, statusKey));
      });
    });
    return { rows, subRows };
  }

  static componentReferences(map, pathPrefix = []) {
    if (!isPlainObject(map)) {
      return [];
    }
    if (FeedComponentsMap.isLeaf(map)) {
      const componentId = positiveId(map.componentId);
      if (!componentId) {
        return [];
      }
      return [{ optionKey: [OPTION_KEY, ...pathPrefix].join("."), componentId }];
    }
    return Object.entries(map).flatMap(([key, node]) => FeedComponentsMap.componentReferences(node, [...pathPrefix, key]));
  }


  static #isLeafIntent(node) {
    return isPlainObject(node) && LEAF_FIELDS.some(field => field in node);
  }

  static #pathLabel(pathPrefix) {
    return pathPrefix.length ? `.${pathPrefix.join(".")}` : "";
  }

  static #isInheritMarker(leaf) {
    return isPlainObject(leaf) && leaf.i === true && !("r" in leaf);
  }

  static #leafProblems(leaf, pathPrefix) {
    const label = `${OPTION_KEY}${FeedComponentsMap.#pathLabel(pathPrefix)}`;
    const problems = [];
    if (!positiveId(leaf.componentId)) {
      problems.push(`${label}.componentId must be a positive component id, got ${JSON.stringify(leaf.componentId)}`);
    }
    const branchKeys = Object.keys(leaf).filter(
      key => !LEAF_FIELDS.includes(key) && !LEGACY_ONLY_FIELDS.includes(key),
    );
    if (branchKeys.length) {
      problems.push(
        `${label} is both a leaf (it has componentId) and a branch (it also has ${branchKeys.join(", ")}) — `
        + `a node maps one component or nests overrides, never both. Move the overrides beside a "${DEFAULT_KEY}" entry.`,
      );
    }
    const sourcing = leaf.requiredContextSourcing;
    if (sourcing !== undefined && !isPlainObject(sourcing)) {
      problems.push(`${label}.requiredContextSourcing must be an object keyed by required-context name`);
      return problems;
    }
    Object.entries(sourcing ?? {}).forEach(([name, entry]) => {
      problems.push(...FeedComponentsMap.#sourcingProblems(`${label}.requiredContextSourcing.${name}`, entry));
    });
    return problems;
  }

  static #sourcingProblems(label, entry) {
    if (!isPlainObject(entry)) {
      return [`${label} must be an object of the form { source, … }`];
    }
    if (!SOURCES.includes(entry.source)) {
      return [`${label}.source "${entry.source}" is not a known source — expected one of ${SOURCES.join(", ")}`];
    }
    if ("valueType" in entry) {
      return FeedComponentsMap.#primitiveSourcingProblems(label, entry);
    }
    return REQUIRED_SOURCE_PARAMS[entry.source]
        .filter(param => entry[param] === undefined || entry[param] === null || entry[param] === "")
        .map(param => `${label} is source "${entry.source}" but has no ${param}`);
  }

  static #resolveEntry({ name, entry, row, scope, walkField }) {
    if (!isPlainObject(entry)) {
      return { problem: `context "${name}" has no sourcing entry` };
    }
    if (entry.valueType) {
      return FeedComponentsMap.#resolvePrimitiveEntry({ name, entry, row, scope, walkField });
    }
    if (entry.source === SOURCE_FEED_ITEM) {
      if (!row?.entityType || !row?.pk) {
        return { problem: `context "${name}" is sourced from the feed item, but this row has no entity pointer` };
      }
      return { value: row.appName ? { entityType: row.entityType, pk: row.pk, appName: row.appName } : { entityType: row.entityType, pk: row.pk } };
    }
    if (entry.source === SOURCE_CONSTANT) {
      return { value: { entityType: entry.entityType, pk: entry.entityPkVars } };
    }
    const base = scope[entry.sourceContext];
    if (base === undefined || base === null) {
      return { deferred: true };
    }
    if (entry.source === SOURCE_CONTEXT) {
      return { value: base };
    }
    if (typeof walkField !== "function") {
      return { problem: `context "${name}" needs a "${entry.sourceField}" field walk, but no field reader is available` };
    }
    const target = walkField(base.entityType, base.pk, entry.sourceField);
    const targetPk = target?.pk ?? target?.entityPk ?? null;
    if (!target || !targetPk) {
      return {
        problem: `context "${name}" is unresolved: "${entry.sourceField}" off "${entry.sourceContext}" `
          + `(${base.entityType}) is empty`,
      };
    }
    return { value: { entityType: target.entityType ?? entry.entityType, pk: targetPk } };
  }


  static #primitiveSourcingFromBinding(binding, rowContextName) {
    const { valueType } = binding;
    if (binding.entitySource === SOURCE_CONSTANT) {
      return { source: SOURCE_CONSTANT, valueType, value: binding.value };
    }
    if (binding.entitySource !== SOURCE_CONTEXT || !binding.sourceContext) {
      return null;
    }
    if (!binding.sourceField) {
      return { source: SOURCE_CONTEXT, valueType, sourceContext: binding.sourceContext };
    }
    if (rowContextName && binding.sourceContext === rowContextName) {
      return { source: SOURCE_FEED_ITEM, valueType, sourceField: binding.sourceField };
    }
    return {
      source: SOURCE_CONTEXT_FIELD,
      valueType,
      sourceContext: binding.sourceContext,
      sourceField: binding.sourceField,
    };
  }

  static #primitiveBindingFromSourcing(sourcing, { rowContextName, valueType }) {
    if (sourcing.source === SOURCE_CONSTANT) {
      return { valueType, entitySource: SOURCE_CONSTANT, value: sourcing.value };
    }
    if (sourcing.source === SOURCE_FEED_ITEM) {
      if (!rowContextName || !sourcing.sourceField) {
        return null;
      }
      return { valueType, entitySource: SOURCE_CONTEXT, sourceContext: rowContextName, sourceField: sourcing.sourceField };
    }
    if (sourcing.source === SOURCE_CONTEXT) {
      return { valueType, entitySource: SOURCE_CONTEXT, sourceContext: sourcing.sourceContext };
    }
    if (sourcing.source === SOURCE_CONTEXT_FIELD) {
      return {
        valueType,
        entitySource: SOURCE_CONTEXT,
        sourceContext: sourcing.sourceContext,
        sourceField: sourcing.sourceField,
      };
    }
    return null;
  }

  static #primitiveSourcingProblems(label, entry) {
    if (!PRIMITIVE_CONTEXT_TYPES.includes(entry.valueType)) {
      return [`${label}.valueType "${entry.valueType}" is not a primitive a feed row can source — `
        + `expected one of ${PRIMITIVE_CONTEXT_TYPES.join(", ")}`];
    }
    const missing = PRIMITIVE_REQUIRED_SOURCE_PARAMS[entry.source]
        .filter(param => entry[param] === undefined || entry[param] === null || entry[param] === "")
        .map(param => `${label} is a ${entry.valueType} "${entry.source}" source but has no ${param}`);
    if (missing.length) {
      return missing;
    }
    if (entry.source === SOURCE_CONSTANT
      && RequiredContextValue.coerce(entry.value, entry.valueType) === undefined) {
      return [`${label}.value ${JSON.stringify(entry.value)} is not a valid ${entry.valueType}`];
    }
    return [];
  }

  static #resolvePrimitiveEntry({ name, entry, row, scope, walkField }) {
    const binding = FeedComponentsMap.#primitiveBindingFromSourcing(entry, {
      rowContextName: FEED_ITEM_SCOPE_KEY,
      valueType: entry.valueType,
    });
    if (!binding) {
      return { problem: `context "${name}" has a ${entry.valueType} "${entry.source}" sourcing entry that names no source` };
    }
    const contexts = {};
    if (entry.source === SOURCE_FEED_ITEM) {
      if (!row?.entityType || !row?.pk) {
        return { problem: `context "${name}" reads the feed item's "${entry.sourceField}", but this row has no entity pointer` };
      }
      contexts[FEED_ITEM_SCOPE_KEY] = { entityType: row.entityType, entityPkUrlVars: row.pk };
    }
    if (entry.source === SOURCE_CONTEXT || entry.source === SOURCE_CONTEXT_FIELD) {
      const base = scope[entry.sourceContext];
      if (base === undefined || base === null) {
        return { deferred: true };
      }
      contexts[entry.sourceContext] = isPlainObject(base) && base.entityType
        ? { entityType: base.entityType, entityPkUrlVars: base.pk ?? base.entityPkUrlVars }
        : base;
    }
    const needsWalk = entry.source === SOURCE_FEED_ITEM || entry.source === SOURCE_CONTEXT_FIELD;
    if (needsWalk && typeof walkField !== "function") {
      return { problem: `context "${name}" needs a "${entry.sourceField}" field read, but no field reader is available` };
    }
    const outcome = RequiredContextValue.resolve(binding, {
      contexts,
      readEntityField: needsWalk ? (entityType, pk, field) => walkField(entityType, pk, field) : undefined,
    });
    if (outcome.reason) {
      return { problem: `context "${name}" (${entry.valueType}) is unresolved: ${outcome.reason}` };
    }
    return { value: outcome.value };
  }

}

module.exports = FeedComponentsMap;
module.exports.default = FeedComponentsMap;
